import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth(); // Note: 'login' now sets the admin user
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetting, setResetting] = useState(false);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/admin/login`,
        { email, password },
        { withCredentials: true }
      );
      login(response.data.user);
      toast.success('Admin login successful!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setResetting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { email: resetEmail });
      toast.success('If the email exists, a reset link will be sent');
      setResetDialogOpen(false);
      setResetEmail('');
    } catch (error) {
      toast.error('Failed to request password reset');
    } finally {
      setResetting(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@400;500&display=swap');

        .login-root {
          min-height: 100vh;
          background: #080808;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'DM Sans', sans-serif;
          overflow: hidden;
          position: relative;
        }

        /* Background geometry */
        .login-root::before {
          content: '';
          position: fixed;
          top: -20%;
          right: -10%;
          width: 600px;
          height: 600px;
          border: 1px solid rgba(220, 38, 38, 0.07);
          transform: rotate(30deg);
          pointer-events: none;
        }

        .login-root::after {
          content: '';
          position: fixed;
          bottom: -15%;
          left: -8%;
          width: 400px;
          height: 400px;
          border: 1px solid rgba(220, 38, 38, 0.05);
          transform: rotate(15deg);
          pointer-events: none;
        }

        .bg-grid {
          position: fixed;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px);
          background-size: 60px 60px;
          pointer-events: none;
        }

        .bg-glow {
          position: fixed;
          top: 20%;
          right: 15%;
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(220,38,38,0.08) 0%, transparent 70%);
          pointer-events: none;
        }

        /* Layout */
        .login-container {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 460px;
          padding: 0 20px;
          animation: fadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* Brand */
        .brand-block {
          margin-bottom: 48px;
        }

        .brand-logo {
          display: flex;
          align-items: baseline;
          gap: 0;
          margin-bottom: 12px;
        }

        .logo-text {
          font-family: 'Bebas Neue', sans-serif;
          font-size: 52px;
          letter-spacing: 0.04em;
          color: #fff;
          line-height: 1;
        }

        .logo-accent {
          font-family: 'Bebas Neue', sans-serif;
          font-size: 52px;
          color: #dc2626;
          line-height: 1;
        }

        .brand-tagline {
          font-family: 'DM Mono', monospace;
          font-size: 10px;
          letter-spacing: 0.25em;
          color: rgba(255,255,255,0.25);
          text-transform: uppercase;
        }

        /* Card */
        .login-card {
          background: #0e0e0e;
          border: 1px solid rgba(255,255,255,0.06);
          padding: 40px;
          position: relative;
          clip-path: polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px));
        }

        /* Red slash accent top-right corner */
        .login-card::before {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 0 20px 20px 0;
          border-color: transparent #dc2626 transparent transparent;
        }

        /* Admin Header inside Card */
        .admin-header {
           font-family: 'DM Mono', monospace;
           font-size: 14px;
           letter-spacing: 0.12em;
           text-transform: uppercase;
           color: #fff;
           margin-bottom: 30px;
           border-bottom: 1px solid rgba(255,255,255,0.06);
           padding-bottom: 12px;
        }

        /* Form elements */
        .field-group {
          margin-bottom: 20px;
        }

        .field-label {
          display: block;
          font-family: 'DM Mono', monospace;
          font-size: 10px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.35);
          margin-bottom: 8px;
        }

        .field-input {
          width: 100%;
          background: #080808 !important;
          border: 1px solid rgba(255,255,255,0.08) !important;
          border-radius: 0 !important;
          color: #fff !important;
          font-family: 'DM Sans', sans-serif !important;
          font-size: 14px !important;
          padding: 12px 14px !important;
          transition: border-color 0.2s !important;
          outline: none !important;
        }

        .field-input:focus {
          border-color: rgba(220, 38, 38, 0.5) !important;
          box-shadow: none !important;
        }

        .field-input::placeholder {
          color: rgba(255,255,255,0.15) !important;
        }

        /* Forgot password */
        .forgot-link {
          background: none;
          border: none;
          font-family: 'DM Mono', monospace;
          font-size: 10px;
          letter-spacing: 0.1em;
          color: #dc2626;
          cursor: pointer;
          padding: 0;
          transition: opacity 0.2s;
          text-transform: uppercase;
        }

        .forgot-link:hover {
          opacity: 0.7;
        }

        /* Primary button */
        .btn-primary {
          width: 100%;
          background: #dc2626 !important;
          border: none !important;
          border-radius: 0 !important;
          color: #fff !important;
          font-family: 'DM Mono', monospace !important;
          font-size: 12px !important;
          letter-spacing: 0.18em !important;
          text-transform: uppercase !important;
          padding: 16px !important;
          cursor: pointer;
          transition: background 0.2s, transform 0.15s !important;
          margin-top: 8px;
          clip-path: polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px));
        }

        .btn-primary:hover:not(:disabled) {
          background: #b91c1c !important;
        }

        .btn-primary:active:not(:disabled) {
          transform: scale(0.99) !important;
        }

        .btn-primary:disabled {
          opacity: 0.5 !important;
          cursor: not-allowed !important;
        }

        /* Dialog overrides */
        .reset-dialog-content {
          background: #0e0e0e !important;
          border: 1px solid rgba(255,255,255,0.08) !important;
          border-radius: 0 !important;
          font-family: 'DM Sans', sans-serif;
        }

        .reset-dialog-title {
          font-family: 'Bebas Neue', sans-serif !important;
          font-size: 28px !important;
          letter-spacing: 0.05em !important;
          color: #fff !important;
        }

        .reset-dialog-desc {
          font-size: 13px;
          color: rgba(255,255,255,0.35);
          line-height: 1.6;
        }

        .forgot-row {
          display: flex;
          justify-content: flex-end;
          margin-top: -6px;
          margin-bottom: 20px;
        }
      `}</style>

      <div className="login-root" data-testid="login-page">
        <div className="bg-grid" />
        <div className="bg-glow" />

        <div className="login-container">
          {/* Brand */}
          <div className="brand-block">
            <div className="brand-logo">
              <span className="logo-text">STUDY&nbsp;POINT</span>
              <span className="logo-accent">.</span>
            </div>
            <div className="brand-tagline">Admin Portal</div>
          </div>

          {/* Card */}
          <div className="login-card">
            <div className="admin-header">
              Admin Login
            </div>

            <form onSubmit={handleAdminLogin}>
              <div className="field-group">
                <label className="field-label">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="field-input"
                  placeholder="admin@example.com"
                  data-testid="admin-email"
                />
              </div>

              <div className="field-group">
                <label className="field-label">Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="field-input"
                  placeholder="••••••••"
                  data-testid="admin-password"
                />
              </div>

              <div className="forgot-row">
                <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                  <DialogTrigger asChild>
                    <button
                      type="button"
                      className="forgot-link"
                      data-testid="forgot-password-link"
                    >
                      Forgot Password?
                    </button>
                  </DialogTrigger>
                  <DialogContent className="reset-dialog-content">
                    <DialogHeader>
                      <DialogTitle className="reset-dialog-title">Reset Password</DialogTitle>
                    </DialogHeader>
                    <div style={{ paddingTop: 16 }}>
                      <p className="reset-dialog-desc" style={{ marginBottom: 20 }}>
                        Enter your admin email and we'll send a reset link.
                      </p>
                      <div className="field-group">
                        <label className="field-label">Email</label>
                        <Input
                          type="email"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          placeholder="admin@example.com"
                          className="field-input"
                          data-testid="reset-email-input"
                        />
                      </div>
                      <Button
                        onClick={handleResetPassword}
                        disabled={resetting}
                        className="btn-primary"
                        data-testid="send-reset-btn"
                      >
                        {resetting ? 'Sending...' : 'Send Reset Link'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="btn-primary"
                data-testid="admin-login-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
